import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Anchor, List, Text, Title } from "@mantine/core";

import { useExtensionStore } from "@prostocheck/data-web";
import { Card, Logo } from "@prostocheck/ui";

import { EXTENSION_GUIDE_INSTALL_STEP_IDS } from "./extension-guide.constants";

/**
 * Explains the jkurwa-web gate browser extension: why it exists (CORS and
 * mixed content block a direct browser request to ЦСК/ДПС, same as
 * `ai/docs/kep-signing.md`'s "The gateway" section) and how to add it from
 * the visitor's browser store. Browsers we do not publish for yet get a
 * "not available for your browser" note instead of the install steps
 * (`useExtensionStore`). Not behind `RequireSession` — reachable from the
 * login page precisely when there is no session yet.
 */
export const ExtensionGuidePage = () => {
  const { t } = useTranslation();
  const { url, isSupported } = useExtensionStore();

  return (
    <div className="relative flex min-h-dvh flex-col items-center justify-center px-5 py-12">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10 bg-linear-to-b from-brand-50 to-surface-muted"
      />

      <Logo className="mb-8" />

      <Card className="w-full max-w-2xl sm:p-8">
        <Title order={1} size="h3">
          {t("extensionGate.guide.title")}
        </Title>

        <Title order={2} size="h5" mt="lg">
          {t("extensionGate.guide.why.title")}
        </Title>
        <Text size="sm" c="dimmed" mt="xs">
          {t("extensionGate.guide.why.description")}
        </Text>

        <Title order={2} size="h5" mt="lg">
          {t("extensionGate.guide.install.title")}
        </Title>

        {isSupported ? (
          <>
            <Text size="sm" c="dimmed" mt="xs">
              {t("extensionGate.guide.install.note")}
            </Text>
            <Anchor
              href={url}
              target="_blank"
              rel="noreferrer"
              size="sm"
              fw={500}
              mt="sm"
              display="inline-block"
            >
              {t("extensionGate.guide.install.storeLink")}
            </Anchor>
            <List type="ordered" size="sm" mt="sm" spacing="xs">
              {EXTENSION_GUIDE_INSTALL_STEP_IDS.map(stepId => (
                <List.Item key={stepId}>
                  {t(`extensionGate.guide.install.steps.${stepId}`)}
                </List.Item>
              ))}
            </List>
          </>
        ) : (
          <Text size="sm" c="dimmed" mt="xs">
            {t("extensionGate.guide.install.unsupportedBrowser")}
          </Text>
        )}

        <Anchor component={Link} to="/login" size="sm" fw={500} mt="lg" display="inline-block">
          {t("extensionGate.guide.back")}
        </Anchor>
      </Card>
    </div>
  );
};
